/**
 * Standardized animation constants for consistent transitions across the site
 * All timings follow the Exora Club luxury aesthetic
 */

export const TRANSITIONS = {
  // Duration classes - OPTIMIZED: 40-60% faster
  fast: 'duration-150',     // Quick feedback (active states, ripples)
  normal: 'duration-200',   // Default for most interactions
  slow: 'duration-300',     // Max duration for any animation
  
  // Easing functions
  ease: 'ease-in-out',      // Standard easing
  easeOut: 'ease-out',      // Deceleration
  easeIn: 'ease-in',        // Acceleration
  
  // Combined classes for common patterns
  standard: 'transition-all duration-200 ease-out',
  quick: 'transition-all duration-150 ease-out',
  elegant: 'transition-all duration-300 ease-in-out',
  
  // Transform-only transitions (better performance)
  transform: 'transition-transform duration-200 ease-out',
  transformFast: 'transition-transform duration-150 ease-out',
} as const;

export const HOVER_EFFECTS = {
  // Scale effects - OPTIMIZED: Subtle scales (max 1.03)
  scale: 'hover:scale-[1.03] active:scale-[0.97]',
  scaleSmall: 'hover:scale-[1.02] active:scale-[0.98]',
  
  // Translate effects
  lift: 'hover:-translate-y-0.5',
  liftLarge: 'hover:-translate-y-1',
  
  // Shadow effects
  glow: 'hover:shadow-lg hover:shadow-gold/30',
  glowStrong: 'hover:shadow-xl hover:shadow-gold/50',
  
  // Combined effects for luxury feel
  luxury: 'hover:scale-[1.03] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gold/30 active:scale-[0.97]',
  luxurySubtle: 'hover:scale-[1.02] hover:shadow-md hover:shadow-gold/20 active:scale-[0.98]',
} as const;

export const ICON_SIZES = {
  xs: 'w-3 h-3',      // 12px - tiny badges, indicators
  sm: 'w-4 h-4',      // 16px - inline text, small buttons, form icons
  md: 'w-5 h-5',      // 20px - default buttons, standard UI
  lg: 'w-6 h-6',      // 24px - prominent features, cards
  xl: 'w-7 h-7',      // 28px - feature cards
  '2xl': 'w-8 h-8',   // 32px - hero sections, headers
  '3xl': 'w-10 h-10', // 40px - large interactive elements
  '4xl': 'w-11 h-11', // 44px - extra large features
  '5xl': 'w-12 h-12', // 48px - feature highlights
  '6xl': 'w-20 h-20', // 80px - success modals, major UI
} as const;

export const TOUCH_TARGETS = {
  // Minimum touch target size for accessibility (44x44px)
  min: 'min-w-[44px] min-h-[44px]',
  comfortable: 'min-w-[48px] min-h-[48px]',
  large: 'min-w-[56px] min-h-[56px]',
} as const;

export const BUTTON_STATES = {
  // Standard button state classes
  base: 'transition-all duration-300 ease-in-out',
  hover: 'hover:scale-105 active:scale-95',
  disabled: 'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
  focus: 'focus:outline-none focus:ring-2 focus:ring-gold/30',
  
  // Combined for complete button styling
  complete: 'transition-all duration-300 ease-in-out hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 focus:outline-none focus:ring-2 focus:ring-gold/30',
} as const;

/**
 * Framer Motion animation variants for consistent animations
 */
export const MOTION_VARIANTS = {
  // Entry/Exit animations - OPTIMIZED: Faster transitions
  fadeIn: {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 10 },
    transition: { duration: 0.2, ease: [0.23, 1, 0.32, 1] },
  },
  
  scaleIn: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    transition: { duration: 0.2, ease: [0.23, 1, 0.32, 1] },
  },
  
  slideUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
    transition: { duration: 0.2, ease: [0.23, 1, 0.32, 1] },
  },
  
  slideDown: {
    initial: { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
    transition: { duration: 0.2, ease: [0.23, 1, 0.32, 1] },
  },
  
  // Interactive states - OPTIMIZED: Subtle scales and faster
  hover: {
    scale: 1.03,
    transition: { duration: 0.15, ease: 'easeOut' },
  },
  
  hoverSubtle: {
    scale: 1.02,
    transition: { duration: 0.15, ease: 'easeOut' },
  },
  
  tap: {
    scale: 0.97,
    transition: { duration: 0.1, ease: 'easeOut' },
  },
  
  // Card animations
  cardHover: {
    y: -4,
    scale: 1.02,
    transition: { duration: 0.18, ease: [0.23, 1, 0.32, 1] },
  },
  
  // Stagger children
  staggerContainer: {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  },
  
  staggerItem: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.2, ease: [0.23, 1, 0.32, 1] },
  },
} as const;

/**
 * Standard transition configuration
 */
export const TRANSITION_CONFIG = {
  duration: 0.2,
  ease: [0.23, 1, 0.32, 1], // Custom easing curve
} as const;
